<script lang="ts" setup>
import { onMounted, ref } from 'vue';

import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { useService } from '@nzyme/vue-ioc/useService.js';
import { useEmitAsync } from '@nzyme/vue-utils/useEmitAsync.js';
import Loader from '@sigma/ui/Loader.vue';
import type { StripeElements } from '@stripe/stripe-js';

import * as l from './StripePaymentForm.loc.js';
import { StripeService } from './StripeService.js';

interface Props {
  clientSecret: string;
}

interface Emits {
  success: [];
  error: [message: string | null];
}

const props = defineProps<Props>();
defineEmits<Emits>();
const emitAsync = useEmitAsync<Emits>();

const stripeService = useService(StripeService);
const t = useTranslate();

const loading = ref(true);
const paymentError = ref<string | null>(null);
const paymentElement = ref<HTMLDivElement>();
const submitting = ref(false);

let elements: StripeElements | null = null;

onMounted(async () => {
  const stripe = await stripeService.getStripe();
  if (!stripe || !paymentElement.value) {
    return;
  }

  elements = stripe.elements({
    clientSecret: props.clientSecret,
  });

  const payment = elements.create('payment');
  payment.mount(paymentElement.value);

  payment.on('ready', () => {
    loading.value = false;
  });
});

async function submit() {
  if (submitting.value || !elements) {
    return;
  }

  const stripe = await stripeService.getStripe();
  if (!stripe) {
    return;
  }

  submitting.value = true;
  paymentError.value = null;

  const { error } = await stripe.confirmPayment({
    elements,
    confirmParams: {
      return_url: window.location.origin + window.location.pathname,
    },
    redirect: 'if_required',
  });

  if (error) {
    const message = error.type === 'validation_error' ? null : (error.message ?? t(l.paymentFailed));
    submitting.value = false;
    await emitAsync('error', message);
  } else {
    await emitAsync('success');
  }
}

defineExpose({ submit });
</script>

<template>
  <div :class="css.container">
    <div
      ref="paymentElement"
      :class="css.element"
    />

    <Loader
      v-if="loading"
      :class="css.loader"
    />
  </div>
</template>

<style lang="scss" module="css">
.container {
  display: flex;
  flex-direction: column;
  gap: $spacing-16;
}

.element {
  min-height: 200px;
}

.loader {
  width: 24px;
  height: 24px;
  color: $color-blue-600;
  margin-inline: auto;
}
</style>
